import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

export const fetchLabSupplies = createAsyncThunk(
  'labSupplies/fetchLabSupplies',
  async (_, { rejectWithValue }) => {
    try {
      const res = await fetch('/api/v1/lab_supplies');
      if (!res.ok) throw new Error('Network response was not ok');
      const data = await res.json();
      return data;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const deleteLabSupply = createAsyncThunk(
  'labSupplies/deleteLabSupply',
  async (id, { rejectWithValue }) => {
    try {
      const res = await fetch(`/api/v1/lab_supplies/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to delete lab supply');
      return id;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const saveLabSupply = createAsyncThunk(
  'labSupplies/saveLabSupply',
  async ({ lab_supply, id }, { rejectWithValue }) => {
    try {
      const url = id ? `/api/v1/lab_supplies/${id}` : '/api/v1/lab_supplies';
      const method = id ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lab_supply }),
      });

      const data = await res.json();
      if (data.errors) throw new Error(data.errors.join(', '));

      return data;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

const labSupplySlice = createSlice({
  name: 'labSupplies',
  initialState: {
    labSupplies: [],
    loading: false,
    error: null,
    submitting: false,
    fetched: false,
  },
  reducers: {
    subscribeLabSupplies(state, action) {
      const { action: wsAction, lab_supply } = action.payload;

      const index = state.labSupplies.findIndex(s => s.id === lab_supply.id);

      if (wsAction === 'created' && index === -1) {
        state.labSupplies.unshift(lab_supply);
      } else if (wsAction === 'updated' && index >= 0) {
        state.labSupplies[index] = {
          ...state.labSupplies[index],
          ...lab_supply
        };
      } else if (wsAction === 'deleted' && index >= 0) {
        state.labSupplies.splice(index, 1);
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLabSupplies.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLabSupplies.fulfilled, (state, action) => {
        state.loading = false;
        state.fetched = true;
        const map = new Map();
        action.payload.forEach(item => {
          map.set(item.id, item);
        });
        state.labSupplies.forEach(item => {
          if (!map.has(item.id)) map.set(item.id, item);
        });
        state.labSupplies = Array.from(map.values());
      })
      .addCase(fetchLabSupplies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.fetched = true;
      })
      .addCase(saveLabSupply.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(saveLabSupply.fulfilled, (state, action) => {
        state.submitting = false;
        const index = state.labSupplies.findIndex((s) => s.id === action.payload.id);
        if (index >= 0) {
          state.labSupplies[index] = action.payload;
        } else {
          state.labSupplies.unshift(action.payload);
        }
      })
      .addCase(saveLabSupply.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload;
      })
      .addCase(deleteLabSupply.fulfilled, (state, action) => {
        state.labSupplies = state.labSupplies.filter(
          (s) => s.id !== action.payload
        );
      });
  },
});

export const { subscribeLabSupplies: subscribeLabSuppliesAction } = labSupplySlice.actions;
export default labSupplySlice.reducer;
